import { useContent } from "contexts/contentContext/contentContext";
import { useState } from "react";
import Icon from "components/Elements/Icon/Icon";
import styles from "./advertisingPolicies.module.scss";

const AdvertisingPolicies = () => {
  const { getTextByKey, getDirection } = useContent();
  const RTLDirection = getDirection() === "rtl";
  const [openSections, setOpenSections] = useState(["hero"]);

  const toggleSection = (section) => {
    setOpenSections((prev) => {
      if (prev.includes(section)) {
        return prev.filter((s) => s !== section);
      } else {
        return [...prev, section];
      }
    });
  };

  const parseContent = (text) => {
    if (!text.includes("<b>")) return text;

    const parts = text.split(/<b>|<\/b>/);
    return parts.map((part, index) => {
      // If the index is odd, then it is text between <b> tags
      if (index % 2 === 1) {
        return <b key={index}>{part}</b>;
      }
      return part;
    });
  };

  const sections = [
    {
      id: "hero",
      title: getTextByKey("advertising-policies-hero-title"),
      contentDescription: [getTextByKey("advertising-policies-hero-content")],
    },
    {
      id: "approach",
      title: getTextByKey("advertising-policies-approach-title"),
      contentDescription: [
        getTextByKey("advertising-policies-approach-content-1"),
        getTextByKey("advertising-policies-approach-content-2"),
        getTextByKey("advertising-policies-approach-content-3"),
        getTextByKey("advertising-policies-approach-content-4"),
      ],
    },
    {
      id: "principles",
      title: getTextByKey("advertising-policies-principles-title"),
      contentDescription: [
        getTextByKey("advertising-policies-principles-content"),
      ],
      contentLists: [
        {
          contentList: [
            getTextByKey("advertising-policies-principles-item-1"),
            getTextByKey("advertising-policies-principles-item-2"),
            getTextByKey("advertising-policies-principles-item-3"),
            getTextByKey("advertising-policies-principles-item-4"),
          ],
        },
      ],
    },
    {
      id: "materials",
      title: getTextByKey("advertising-policies-materials-title"),
      contentDescription: [
        getTextByKey("advertising-policies-materials-content"),
      ],
    },
    {
      id: "terms",
      title: getTextByKey("advertising-policies-terms-title"),
      contentDescription: [getTextByKey("advertising-policies-terms-content")],
    },
    {
      id: "prohibited-content",
      title: getTextByKey("advertising-policies-prohibited-content-title"),
      contentDescription: [
        getTextByKey("advertising-policies-prohibited-content-content"),
      ],
      contentLists: [
        {
          contentListTitle: getTextByKey(
            "advertising-policies-prohibited-content-list-title-1"
          ),
          contentList: [
            getTextByKey("advertising-policies-prohibited-content-item-1"),
            getTextByKey("advertising-policies-prohibited-content-item-2"),
            getTextByKey("advertising-policies-prohibited-content-item-3"),
            getTextByKey("advertising-policies-prohibited-content-item-4"),
            getTextByKey("advertising-policies-prohibited-content-item-5"),
            getTextByKey("advertising-policies-prohibited-content-item-6"),
            getTextByKey("advertising-policies-prohibited-content-item-7"),
          ],
        },
        {
          contentListTitle: getTextByKey(
            "advertising-policies-prohibited-content-list-title-2"
          ),
          contentList: [
            getTextByKey("advertising-policies-prohibited-content-item-8"),
            getTextByKey("advertising-policies-prohibited-content-item-9"),
            getTextByKey("advertising-policies-prohibited-content-item-10"),
            getTextByKey("advertising-policies-prohibited-content-item-11"),
            getTextByKey("advertising-policies-prohibited-content-item-12"),
            getTextByKey("advertising-policies-prohibited-content-item-13"),
            getTextByKey("advertising-policies-prohibited-content-item-14"),
            getTextByKey("advertising-policies-prohibited-content-item-15"),
          ],
        },
      ],
    },
    {
      id: "restricted-content",
      title: getTextByKey("advertising-policies-restricted-content-title"),
      contentDescription: [
        getTextByKey("advertising-policies-restricted-content-content"),
      ],
      contentLists: [
        {
          contentListTitle: getTextByKey(
            "advertising-policies-restricted-content-list-title"
          ),
          contentList: [
            getTextByKey("advertising-policies-restricted-content-item-1"),
            getTextByKey("advertising-policies-restricted-content-item-2"),
            getTextByKey("advertising-policies-restricted-content-item-3"),
            getTextByKey("advertising-policies-restricted-content-item-4"),
            getTextByKey("advertising-policies-restricted-content-item-5"),
            getTextByKey("advertising-policies-restricted-content-item-6"),
            getTextByKey("advertising-policies-restricted-content-item-7"),
            getTextByKey("advertising-policies-restricted-content-item-8"),
          ],
        },
      ],
    },
    {
      id: "territories-parties",
      title: getTextByKey("advertising-policies-territories-parties-title"),
      contentDescription: [
        getTextByKey("advertising-policies-territories-parties-content"),
      ],
    },
    {
      id: "general-terms",
      title: getTextByKey("advertising-policies-terms-title"),
      contentDescription: [
        getTextByKey("advertising-policies-terms-content-2"),
      ],
    },
  ];

  const handleKeyDown = (e, idx) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = document.getElementById(
        `accordion-header-${sections[(idx + 1) % sections.length].id}`
      );
      next && next.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIdx = (idx - 1 + sections.length) % sections.length;
      const prev = document.getElementById(
        `accordion-header-${sections[prevIdx].id}`
      );
      prev && prev.focus();
    }
  };

  return (
    <div className={styles.policiesSection}>
      <div className={styles.policiesContainer}>
        <h2 className={styles.mainTitle}>
          {getTextByKey("advertising-policies-title")}
        </h2>
        <p className={styles.subtitle}>
          {getTextByKey("advertising-policies-subtitle")}
        </p>

        <div className={styles.accordionContainer}>
          {sections.map((section, idx) => (
            <div
              key={section.id}
              className={`${styles.accordionItem} ${
                openSections.includes(section.id) ? styles.active : ""
              }`}
            >
              <button
                className={styles.accordionHeader}
                onClick={() => toggleSection(section.id)}
                aria-expanded={openSections.includes(section.id)}
                aria-controls={`accordion-content-${section.id}`}
                id={`accordion-header-${section.id}`}
                onKeyDown={(e) => handleKeyDown(e, idx)}
              >
                <span>{section.title}</span>
                <Icon
                  name="newinbox/arrow_down"
                  iconClass={`${styles.arrow} ${
                    openSections.includes(section.id) ? styles.rotated : ""
                  }`}
                />
              </button>
              <div
                className={`${styles.accordionContent} ${
                  openSections.includes(section.id) ? styles.expanded : ""
                }`}
                id={`accordion-content-${section.id}`}
                role="region"
                aria-labelledby={`accordion-header-${section.id}`}
              >
                <div className={styles.contentInner}>
                  {section.contentDescription.map((description, index) => (
                    <p
                      key={`${section.id}-content-${index}`}
                      className={styles.contentDescription}
                    >
                      {parseContent(description)}
                    </p>
                  ))}

                  {section.contentLists && (
                    <div className={styles.contentLists}>
                      {section.contentLists.map((list, index) => (
                        <div
                          key={`${section.id}-list-${index}`}
                          className={styles.listContainer}
                        >
                          {list.contentListTitle && (
                            <h3 className={styles.listTitle}>
                              {parseContent(list.contentListTitle)}
                            </h3>
                          )}
                          <ul className={styles.contentList}>
                            {list.contentList.map((item, itemIndex) => (
                              <li
                                key={`${section.id}-list-${index}-item-${itemIndex}`}
                                className={`${styles.listItem} ${
                                  RTLDirection ? styles.rtlListItem : ""
                                }`}
                              >
                                {parseContent(item)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdvertisingPolicies;
